import React, { useMemo } from 'react';
import {
  HiddenContent,
  SideNavLinkFeaturesSlot,
  SideNavLinkInnerWrapper,
  SideNavLinkOuterWrapper,
  SideNavLinkTitleWrapper,
  SvgWrapper,
} from './styles';
import { SvgIcon } from 'src/commons/components/SvgIcon/SvgIcon';
import { useConsolidatedNavContext } from 'src/providers/ConsolidatedNavContextProvider';
import { NavMetricPublisher } from 'src/commons/utils/metrics';
import { publishNavLinkClick } from 'src/features/nav/utils/navLinkClick';
import SideNavLinkFeatures from 'src/features/nav/components/ConsolidatedSideNav/SideNavLinkFeatures/SideNavLinkFeatures';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import { CC_MODAL_TOOLBAR_ID } from 'src/commons/constants';
import { useInterstitialModalIntercept } from './InterstitialModal/useInterstitialModalIntercept';
import { getFirstSideNavLink } from 'src/features/nav/utils/toolbarItemLinks';

interface SideNavLinkProps {
  id: string;
  isQuickNavModalEnabled?: boolean;
  allToolsEnabled: boolean;
}

const SideNavLink: React.VFC<SideNavLinkProps> = ({
  id,
  isQuickNavModalEnabled = false,
  allToolsEnabled,
}) => {
  const { isNavOpen, selectedNavItemId, toolbarItems, navCohort } =
    useConsolidatedNavContext();
  const tryInterceptInterstitial = useInterstitialModalIntercept();
  const isSelected = useMemo(
    () =>
      selectedNavItemId === id ||
      (selectedNavItemId === CC_MODAL_TOOLBAR_ID &&
        id === 'aac-toolbar-item-sponsored-ads' &&
        !isQuickNavModalEnabled),
    [selectedNavItemId],
  );
  const toolbarItem = useMemo(
    () => toolbarItems.find((toolbarItem) => toolbarItem.id === id),
    [toolbarItems],
  );

  const targetUrl = toolbarItem?.sidebarGroups?.[0]?.links?.[0]?.link?.url;
  const appName = toolbarItem?.sidebarGroups?.[0]?.links?.[0]?.appName || '';
  const linkId = toolbarItem?.sidebarGroups?.[0]?.links?.[0]?.id ?? id;

  const onClickHandler = (e: React.MouseEvent<HTMLAnchorElement>) => {
    NavMetricPublisher.publishCounterMonitor(`toolbarItemClicked-${id}`, 1);
    publishNavLinkClick(linkId, navCohort);
    tryInterceptInterstitial(targetUrl, appName, e);
  };

  if (!toolbarItem) {
    return null;
  }

  const firstLink = getFirstSideNavLink(toolbarItem);
  if (!firstLink) {
    return null;
  }
  const { link } = firstLink;
  const { label: title, url } = link;

  return (
    <SideNavLinkOuterWrapper isSelected={isSelected} isNavOpen={isNavOpen}>
      <SideNavLinkInnerWrapper
        isNavOpen={isNavOpen}
        isSelected={isSelected}
        href={url}
        data-ccx-e2e-id={linkId}
        aria-label={title}
        onClick={onClickHandler}
        {...(isSelected && { ariaCurrent: 'page' })}
        {...getTaktAttributes(
          linkId,
          chromeComponents.NAV,
          { url: url },
          allToolsEnabled,
        )}
      >
        <SvgWrapper
          isSelected={isSelected}
          data-ccx-e2e-id={id}
          alignItems="center"
          justifyContent="center"
        >
          <SvgIcon
            iconPath={toolbarItem.iconPath}
            ariaLabel={title}
            isSelected={isSelected}
          />
        </SvgWrapper>
        <HiddenContent alignItems="center">
          <SideNavLinkTitleWrapper>{title}</SideNavLinkTitleWrapper>
        </HiddenContent>
      </SideNavLinkInnerWrapper>
      <SideNavLinkFeaturesSlot alignItems="center">
        <SideNavLinkFeatures sideNavLink={firstLink} isFocusable />
      </SideNavLinkFeaturesSlot>
    </SideNavLinkOuterWrapper>
  );
};

export default SideNavLink;
